export type StoreLocaleOption = {
  code: string;
  name: string;
  flag: string;
  countryCode?: string;
  speakers?: string;
  supportsGooglePlay: boolean;
  supportsAppStore: boolean;
};

export type StoreLocaleSupportFilter =
  | "boiler_plate"
  | "common"
  | "google_play"
  | "app_store"
  | "all";

export const BOILER_PLATE_AVAILABLE_LANGUAGES = [
  {
    code: "en",
    name: "English",
    flag: "🇬🇧",
    countryCode: "GB",
    speakers: "1.5B",
  },
  {
    code: "zh",
    name: "Chinese (Simplified)",
    flag: "🇨🇳",
    countryCode: "CN",
    speakers: "1.1B",
  },
  {
    code: "hi",
    name: "Hindi",
    flag: "🇮🇳",
    countryCode: "IN",
    speakers: "602M",
  },
  {
    code: "es",
    name: "Spanish",
    flag: "🇪🇸",
    countryCode: "ES",
    speakers: "559M",
  },
  {
    code: "fr",
    name: "French",
    flag: "🇫🇷",
    countryCode: "FR",
    speakers: "280M",
  },
  {
    code: "ar",
    name: "Arabic",
    flag: "🇸🇦",
    countryCode: "SA",
    speakers: "274M",
  },
  {
    code: "bn",
    name: "Bengali",
    flag: "🇧🇩",
    countryCode: "BD",
    speakers: "272M",
  },
  {
    code: "pt",
    name: "Portuguese",
    flag: "🇵🇹",
    countryCode: "PT",
    speakers: "264M",
  },
  {
    code: "ru",
    name: "Russian",
    flag: "🇷🇺",
    countryCode: "RU",
    speakers: "258M",
  },
  {
    code: "ja",
    name: "Japanese",
    flag: "🇯🇵",
    countryCode: "JP",
    speakers: "125M",
  },
  {
    code: "de",
    name: "German",
    flag: "🇩🇪",
    countryCode: "DE",
    speakers: "134M",
  },
  {
    code: "ko",
    name: "Korean",
    flag: "🇰🇷",
    countryCode: "KR",
    speakers: "81M",
  },
  {
    code: "it",
    name: "Italian",
    flag: "🇮🇹",
    countryCode: "IT",
    speakers: "68M",
  },
  {
    code: "tr",
    name: "Turkish",
    flag: "🇹🇷",
    countryCode: "TR",
    speakers: "88M",
  },
  {
    code: "vi",
    name: "Vietnamese",
    flag: "🇻🇳",
    countryCode: "VN",
    speakers: "85M",
  },
] as const;

const BOILER_PLATE_LOCALE_CODES = new Set(
  BOILER_PLATE_AVAILABLE_LANGUAGES.map((locale) => locale.code.toLowerCase()),
);

const STORE_LOCALE_CATALOG: StoreLocaleOption[] = [
  {
    code: "en",
    name: "English",
    flag: "🇺🇸",
    speakers: "1.5B",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "en-US",
    name: "English (United States)",
    flag: "🇺🇸",
    speakers: "1.5B",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "en-GB",
    name: "English (United Kingdom)",
    flag: "🇬🇧",
    speakers: "1.5B",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "en-AU",
    name: "English (Australia)",
    flag: "🇦🇺",
    speakers: "1.5B",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "en-CA",
    name: "English (Canada)",
    flag: "🇨🇦",
    speakers: "1.5B",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "en-IN",
    name: "English (India)",
    flag: "🇮🇳",
    speakers: "1.5B",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "en-SG",
    name: "English (Singapore)",
    flag: "🇸🇬",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "en-ZA",
    name: "English (South Africa)",
    flag: "🇿🇦",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "zh",
    name: "Chinese (Simplified)",
    flag: "🇨🇳",
    speakers: "1.1B",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "zh-CN",
    name: "Chinese (Simplified)",
    flag: "🇨🇳",
    speakers: "1.1B",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "zh-Hans",
    name: "Chinese (Simplified)",
    flag: "🇨🇳",
    speakers: "1.1B",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "zh-TW",
    name: "Chinese (Traditional)",
    flag: "🇹🇼",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "zh-Hant",
    name: "Chinese (Traditional)",
    flag: "🇹🇼",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "zh-HK",
    name: "Chinese (Hong Kong)",
    flag: "🇭🇰",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "hi",
    name: "Hindi",
    flag: "🇮🇳",
    speakers: "602M",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "hi-IN",
    name: "Hindi",
    flag: "🇮🇳",
    speakers: "602M",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "es",
    name: "Spanish",
    flag: "🇪🇸",
    speakers: "559M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "es-419",
    name: "Spanish (Latin America)",
    flag: "🌎",
    speakers: "559M",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "es-ES",
    name: "Spanish (Spain)",
    flag: "🇪🇸",
    speakers: "559M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "es-MX",
    name: "Spanish (Mexico)",
    flag: "🇲🇽",
    speakers: "559M",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "es-US",
    name: "Spanish (United States)",
    flag: "🇺🇸",
    speakers: "559M",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "fr",
    name: "French",
    flag: "🇫🇷",
    speakers: "280M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "fr-FR",
    name: "French (France)",
    flag: "🇫🇷",
    speakers: "280M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "fr-CA",
    name: "French (Canada)",
    flag: "🇨🇦",
    speakers: "280M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "ar",
    name: "Arabic",
    flag: "🇸🇦",
    speakers: "274M",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ar-SA",
    name: "Arabic",
    flag: "🇸🇦",
    speakers: "274M",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "bn",
    name: "Bangla",
    flag: "🇧🇩",
    speakers: "272M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "bn-BD",
    name: "Bangla",
    flag: "🇧🇩",
    speakers: "272M",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "pt",
    name: "Portuguese",
    flag: "🇵🇹",
    speakers: "264M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "pt-BR",
    name: "Portuguese (Brazil)",
    flag: "🇧🇷",
    speakers: "264M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "pt-PT",
    name: "Portuguese (Portugal)",
    flag: "🇵🇹",
    speakers: "264M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "ru",
    name: "Russian",
    flag: "🇷🇺",
    speakers: "258M",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "ru-RU",
    name: "Russian",
    flag: "🇷🇺",
    speakers: "258M",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ja",
    name: "Japanese",
    flag: "🇯🇵",
    speakers: "125M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "ja-JP",
    name: "Japanese",
    flag: "🇯🇵",
    speakers: "125M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "de",
    name: "German",
    flag: "🇩🇪",
    speakers: "134M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "de-DE",
    name: "German",
    flag: "🇩🇪",
    speakers: "134M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "ko",
    name: "Korean",
    flag: "🇰🇷",
    speakers: "81M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "ko-KR",
    name: "Korean",
    flag: "🇰🇷",
    speakers: "81M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "it",
    name: "Italian",
    flag: "🇮🇹",
    speakers: "68M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "it-IT",
    name: "Italian",
    flag: "🇮🇹",
    speakers: "68M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "tr",
    name: "Turkish",
    flag: "🇹🇷",
    speakers: "88M",
    supportsGooglePlay: false,
    supportsAppStore: false,
  },
  {
    code: "tr-TR",
    name: "Turkish",
    flag: "🇹🇷",
    speakers: "88M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "vi",
    name: "Vietnamese",
    flag: "🇻🇳",
    speakers: "85M",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "af",
    name: "Afrikaans",
    flag: "🇿🇦",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "sq",
    name: "Albanian",
    flag: "🇦🇱",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "am",
    name: "Amharic",
    flag: "🇪🇹",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "hy-AM",
    name: "Armenian",
    flag: "🇦🇲",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "az-AZ",
    name: "Azerbaijani",
    flag: "🇦🇿",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "eu-ES",
    name: "Basque",
    flag: "🇪🇸",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "be",
    name: "Belarusian",
    flag: "🇧🇾",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "bg",
    name: "Bulgarian",
    flag: "🇧🇬",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "my-MM",
    name: "Burmese",
    flag: "🇲🇲",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ca",
    name: "Catalan",
    flag: "🇪🇸",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "hr",
    name: "Croatian",
    flag: "🇭🇷",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "cs-CZ",
    name: "Czech",
    flag: "🇨🇿",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "da-DK",
    name: "Danish",
    flag: "🇩🇰",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "nl-NL",
    name: "Dutch",
    flag: "🇳🇱",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "et",
    name: "Estonian",
    flag: "🇪🇪",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "fil",
    name: "Filipino",
    flag: "🇵🇭",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "fi-FI",
    name: "Finnish",
    flag: "🇫🇮",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "gl-ES",
    name: "Galician",
    flag: "🇪🇸",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ka-GE",
    name: "Georgian",
    flag: "🇬🇪",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "el-GR",
    name: "Greek",
    flag: "🇬🇷",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "gu",
    name: "Gujarati",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "iw-IL",
    name: "Hebrew",
    flag: "🇮🇱",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "he-IL",
    name: "Hebrew",
    flag: "🇮🇱",
    supportsGooglePlay: false,
    supportsAppStore: true,
  },
  {
    code: "hu-HU",
    name: "Hungarian",
    flag: "🇭🇺",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "is-IS",
    name: "Icelandic",
    flag: "🇮🇸",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "id",
    name: "Indonesian",
    flag: "🇮🇩",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "kn-IN",
    name: "Kannada",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "kk",
    name: "Kazakh",
    flag: "🇰🇿",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "km-KH",
    name: "Khmer",
    flag: "🇰🇭",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ky-KG",
    name: "Kyrgyz",
    flag: "🇰🇬",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "lo-LA",
    name: "Lao",
    flag: "🇱🇦",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "lv",
    name: "Latvian",
    flag: "🇱🇻",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "lt",
    name: "Lithuanian",
    flag: "🇱🇹",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "mk-MK",
    name: "Macedonian",
    flag: "🇲🇰",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ms-MY",
    name: "Malay (Malaysia)",
    flag: "🇲🇾",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ms",
    name: "Malay",
    flag: "🇲🇾",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "ml-IN",
    name: "Malayalam",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "mr-IN",
    name: "Marathi",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "mn-MN",
    name: "Mongolian",
    flag: "🇲🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ne-NP",
    name: "Nepali",
    flag: "🇳🇵",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "no-NO",
    name: "Norwegian",
    flag: "🇳🇴",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "fa",
    name: "Persian",
    flag: "🇮🇷",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "fa-AE",
    name: "Persian (U.A.E.)",
    flag: "🇦🇪",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "fa-AF",
    name: "Persian (Afghanistan)",
    flag: "🇦🇫",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "fa-IR",
    name: "Persian (Iran)",
    flag: "🇮🇷",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "pl-PL",
    name: "Polish",
    flag: "🇵🇱",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "pa",
    name: "Punjabi",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "ro",
    name: "Romanian",
    flag: "🇷🇴",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "rm",
    name: "Romansh",
    flag: "🇨🇭",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "sr",
    name: "Serbian",
    flag: "🇷🇸",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "si-LK",
    name: "Sinhala",
    flag: "🇱🇰",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "sk",
    name: "Slovak",
    flag: "🇸🇰",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "sl",
    name: "Slovenian",
    flag: "🇸🇮",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "sw",
    name: "Swahili",
    flag: "🇰🇪",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "sv-SE",
    name: "Swedish",
    flag: "🇸🇪",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "ta-IN",
    name: "Tamil",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "te-IN",
    name: "Telugu",
    flag: "🇮🇳",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "th",
    name: "Thai",
    flag: "🇹🇭",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "uk",
    name: "Ukrainian",
    flag: "🇺🇦",
    supportsGooglePlay: true,
    supportsAppStore: true,
  },
  {
    code: "ur",
    name: "Urdu",
    flag: "🇵🇰",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
  {
    code: "zu",
    name: "Zulu",
    flag: "🇿🇦",
    supportsGooglePlay: true,
    supportsAppStore: false,
  },
];

const uniqueByCode = (locales: StoreLocaleOption[]) =>
  locales.filter(
    (locale, index, collection) =>
      collection.findIndex(
        (candidate) =>
          candidate.code.toLowerCase() === locale.code.toLowerCase(),
      ) === index,
  );

const APP_STORE_CONNECT_SUPPORTED_LOCALE_CODES = new Set([
  "ar-SA",
  "ca",
  "cs",
  "da",
  "de-DE",
  "el",
  "en-AU",
  "en-CA",
  "en-GB",
  "en-US",
  "es-ES",
  "es-MX",
  "fi",
  "fr-CA",
  "fr-FR",
  "he",
  "hi",
  "hr",
  "hu",
  "id",
  "it",
  "ja",
  "ko",
  "ms",
  "nl-NL",
  "no",
  "pl",
  "pt-BR",
  "pt-PT",
  "ro",
  "ru",
  "sk",
  "sv",
  "th",
  "tr",
  "uk",
  "vi",
  "zh-Hans",
  "zh-Hant",
]);

const normalizeLocaleCode = (localeCode: string) =>
  String(localeCode || "")
    .trim()
    .replaceAll("_", "-")
    .split("-")
    .filter(Boolean)
    .map((part, index) => {
      if (index === 0) return part.toLowerCase();
      if (part.length === 2) return part.toUpperCase();
      return `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`;
    })
    .join("-");

export const toAppStoreConnectLocaleCode = (localeCode: string) => {
  const normalized = normalizeLocaleCode(localeCode);
  if (!normalized) return "";

  const explicitMap: Record<string, string> = {
    "cs-CZ": "cs",
    "da-DK": "da",
    "el-GR": "el",
    "fi-FI": "fi",
    "he-IL": "he",
    "hu-HU": "hu",
    "it-IT": "it",
    "ja-JP": "ja",
    "ko-KR": "ko",
    "no-NO": "no",
    "pl-PL": "pl",
    "sv-SE": "sv",
    "tr-TR": "tr",
    "zh-CN": "zh-Hans",
    "zh-HK": "zh-Hant",
    "zh-TW": "zh-Hant",
  };

  if (explicitMap[normalized]) return explicitMap[normalized];
  if (APP_STORE_CONNECT_SUPPORTED_LOCALE_CODES.has(normalized)) {
    return normalized;
  }

  const [languageCode] = normalized.split("-");
  if (APP_STORE_CONNECT_SUPPORTED_LOCALE_CODES.has(languageCode)) {
    return languageCode;
  }

  const languageDefaults: Record<string, string> = {
    ar: "ar-SA",
    de: "de-DE",
    en: "en-US",
    es: "es-ES",
    fr: "fr-FR",
    nl: "nl-NL",
    pt: "pt-BR",
    zh: "zh-Hans",
  };
  if (languageDefaults[languageCode]) {
    return languageDefaults[languageCode];
  }

  return normalized;
};

export const isSupportedAppStoreConnectLocale = (localeCode: string) =>
  APP_STORE_CONNECT_SUPPORTED_LOCALE_CODES.has(
    toAppStoreConnectLocaleCode(localeCode),
  );

export const ALL_STORE_LOCALES = uniqueByCode(STORE_LOCALE_CATALOG);

export const GOOGLE_PLAY_STORE_LOCALES = ALL_STORE_LOCALES.filter(
  (locale) => locale.supportsGooglePlay,
);

export const APP_STORE_LISTING_LOCALES = uniqueByCode(
  ALL_STORE_LOCALES.filter((locale) => locale.supportsAppStore)
    .map((locale) => ({
      ...locale,
      code: toAppStoreConnectLocaleCode(locale.code),
    }))
    .filter((locale) => isSupportedAppStoreConnectLocale(locale.code)),
);

export const STORE_LOCALE_SELECTION_OPTIONS = ALL_STORE_LOCALES;

export const getStoreLocaleOption = (localeCode: string) => {
  const normalized = String(localeCode || "")
    .trim()
    .toLowerCase();
  const directMatch = ALL_STORE_LOCALES.find(
    (locale) => locale.code.toLowerCase() === normalized,
  );
  if (directMatch) return directMatch;

  const appStoreNormalized = toAppStoreConnectLocaleCode(localeCode).toLowerCase();
  if (!appStoreNormalized) return undefined;

  return APP_STORE_LISTING_LOCALES.find(
    (locale) => locale.code.toLowerCase() === appStoreNormalized,
  );
};

export const getStoreLocaleDisplayLabel = (localeCode: string) => {
  const locale = getStoreLocaleOption(localeCode);
  const normalizedCode = String(localeCode || "").trim();
  if (!normalizedCode) {
    return "Unknown locale";
  }
  return locale
    ? `${locale.code.toUpperCase()} - ${locale.name}`
    : normalizedCode.toUpperCase();
};

export const filterStoreLocalesBySupport = (
  locales: StoreLocaleOption[],
  filter: StoreLocaleSupportFilter,
) => {
  const normalizeAppStoreLocales = (input: StoreLocaleOption[]) =>
    uniqueByCode(
      input
        .filter((locale) => locale.supportsAppStore)
        .map((locale) => ({
          ...locale,
          code: toAppStoreConnectLocaleCode(locale.code),
        }))
        .filter((locale) => isSupportedAppStoreConnectLocale(locale.code)),
    );

  switch (filter) {
    case "boiler_plate":
      return locales.filter((locale) =>
        BOILER_PLATE_LOCALE_CODES.has(locale.code.toLowerCase()),
      );
    case "common":
      return locales.filter(
        (locale) => locale.supportsGooglePlay && locale.supportsAppStore,
      );
    case "google_play":
      return locales.filter((locale) => locale.supportsGooglePlay);
    case "app_store":
      return normalizeAppStoreLocales(locales);
    case "all":
    default:
      return locales;
  }
};
